import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { postInquiry } from '../../api/inquiryService';
import { getSggList, getSidoList } from '../../api/regionService';
import styles from './InquiryForm.module.css';

const CONSENT_TEXT = {
  purposeTitle: '개인정보 수집 및 이용목적:',
  purposeBody: '가맹 모집에 대한 지원 문의 또는 관련한 정보 안내 등',
  itemsTitle: '개인정보 수집항목:',
  itemsBody: '[필수] 이름, 연락처(휴대 전화번호), 이메일 주소, 1차 희망 지역, 2차 희망 지역',
  itemsNote: '※ 필수항목은 온라인 지원에 필요한 최소한의 정보로 동의해주셔야 지원이 가능합니다.',
  retentionTitle: '보유 근거 및 보유기간 :',
  retentionBody: [
    '개인정보 수집 및 이용 목적이 달성된 후 180일 보관 후 파기',
    '단, 전자상거래의 소비자보호에 관한 법률 등 타 법률에 의해 보존할 필요가 있는 경우',
    '일정 기간 보존 시 가맹 문의 및 신청이 불가능합니다.',
  ],
  refusalTitle: '※ 동의 거부 시 불이익에 관한 사항',
  refusalBody: [
    '이용자는 개인정보의 수집 및 동의를 거부할 권리가 있습니다.',
    '수집하는 최소한의 개인정보, 즉, 필수 항목에 대한 수집 및 이용 동의를 거부하실 경우, 지원이 어려울 수 있습니다.',
  ],
};

const INITIAL_FORM = {
  name: '',
  email: '',
  hopeSidoCd1: '',
  hopeSggCd1: '',
  hopeSidoCd2: '',
  hopeSggCd2: '',
  message: '',
  agree: false,
};

/**
 * 가맹문의 신청서 — 기존에는 구글폼(외부 링크)으로 이동시켰지만, 지금은 동일한
 * 입력 항목(이름/연락처/이메일/1·2차 희망지역/가맹문의 내용 + 개인정보 수집 동의)을
 * 사이트 안에서 직접 입력받아 WWW 백엔드(POST /api/inquiries, inquiryType=FR)로
 * 전송한다. 본부 지원 신청서(ApplyForm)와 같은 테이블·API를 inquiryType으로만 구분해 쓴다.
 *
 * 희망지역은 2026-08-04부터 자유텍스트가 아니라 시도/시군구 드롭다운(코드값)으로 받는다
 * (BR_MST/FR_MST와 동일한 CODE_MST CD_GROUP=REGION 코드 체계, GET /api/regions/sido·sgg).
 *
 * 연락처는 010-1234-5678 형태로 저장하지만, 입력은 3칸(010 / 1234 / 5678)으로 나눠 받는다
 * — 각 칸을 채우면 자동으로 다음 칸에 포커스가 넘어간다. 제출 시 세 칸을 '-'로 이어붙여
 * WRITER_TEL(=phone)에 그 형식 그대로 저장한다.
 */
export default function InquiryForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState(INITIAL_FORM);
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const phone2Ref = useRef(null);
  const phone3Ref = useRef(null);
  const [sidoList, setSidoList] = useState([]);
  const [sggList1, setSggList1] = useState([]);
  const [sggList2, setSggList2] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    getSidoList()
      .then(setSidoList)
      .catch(() => setSidoList([]));
  }, []);

  const update = (field) => (e) => {
    const value = field === 'agree' ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhonePart = (setPart, maxLen, nextRef) => (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, maxLen);
    setPart(digits);
    if (digits.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  const handleSidoChange = (which) => async (e) => {
    const sidoCd = e.target.value;
    const sggField = which === 1 ? 'hopeSggCd1' : 'hopeSggCd2';
    const sidoField = which === 1 ? 'hopeSidoCd1' : 'hopeSidoCd2';
    const setSggList = which === 1 ? setSggList1 : setSggList2;
    setForm((prev) => ({ ...prev, [sidoField]: sidoCd, [sggField]: '' }));
    if (!sidoCd) {
      setSggList([]);
      return;
    }
    try {
      setSggList(await getSggList(sidoCd));
    } catch {
      setSggList([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      !form.name.trim() ||
      phone1.length !== 3 ||
      phone2.length !== 4 ||
      phone3.length !== 4 ||
      !form.email.trim() ||
      !form.hopeSidoCd1 ||
      !form.hopeSggCd1 ||
      !form.hopeSidoCd2 ||
      !form.hopeSggCd2 ||
      !form.message.trim()
    ) {
      setError('필수 항목을 모두 입력해 주세요.');
      return;
    }
    if (!form.agree) {
      setError('개인정보 수집 및 이용에 동의해 주세요.');
      return;
    }

    setSubmitting(true);
    try {
      await postInquiry({
        inquiryType: 'FR',
        name: form.name,
        phone: `${phone1}-${phone2}-${phone3}`,
        email: form.email,
        hopeSidoCd1: form.hopeSidoCd1,
        hopeSggCd1: form.hopeSggCd1,
        hopeSidoCd2: form.hopeSidoCd2,
        hopeSggCd2: form.hopeSggCd2,
        content: form.message,
        privacyAgree: form.agree,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || '신청 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (submitted) {
    return (
      <div className={styles.done}>
        <div className={styles.doneIco} aria-hidden="true">
          ✓
        </div>
        <h3>신청이 접수되었습니다</h3>
        <p>
          {form.name} 님, 가맹문의 신청이 정상적으로 접수되었습니다.
          <br />
          가맹 상담 직원이 확인 후 빠르게 연락드리겠습니다.
        </p>
        <button type="button" className={styles.doneBtn} onClick={() => navigate('/')}>
          홈으로 이동
        </button>
      </div>
    );
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <div className={styles.field}>
        <label htmlFor="iq-name">
          이름 <span className={styles.req}>*</span>
        </label>
        <input
          id="iq-name"
          type="text"
          maxLength={20}
          placeholder="이름을 입력해 주세요"
          value={form.name}
          onChange={update('name')}
        />
      </div>

      <div className={styles.field}>
        <label htmlFor="iq-phone1">
          연락처 <span className={styles.req}>*</span>
        </label>
        <div className={styles.phoneRow}>
          <input
            id="iq-phone1"
            type="tel"
            inputMode="numeric"
            maxLength={3}
            placeholder="010"
            value={phone1}
            onChange={handlePhonePart(setPhone1, 3, phone2Ref)}
          />
          <span className={styles.phoneDash}>-</span>
          <input
            ref={phone2Ref}
            type="tel"
            inputMode="numeric"
            maxLength={4}
            placeholder="1234"
            value={phone2}
            onChange={handlePhonePart(setPhone2, 4, phone3Ref)}
          />
          <span className={styles.phoneDash}>-</span>
          <input
            ref={phone3Ref}
            type="tel"
            inputMode="numeric"
            maxLength={4}
            placeholder="5678"
            value={phone3}
            onChange={handlePhonePart(setPhone3, 4, null)}
          />
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="iq-email">
          이메일 <span className={styles.req}>*</span>
        </label>
        <input id="iq-email" type="email" placeholder="example@email.com" value={form.email} onChange={update('email')} />
      </div>

      <div className={styles.fieldRow}>
        <div className={styles.field}>
          <label htmlFor="iq-sido1">
            1차 희망지역 <span className={styles.req}>*</span>
          </label>
          <div className={styles.regionSelectRow}>
            <select id="iq-sido1" value={form.hopeSidoCd1} onChange={handleSidoChange(1)}>
              <option value="">시/도 선택</option>
              {sidoList.map((sido) => (
                <option key={sido.cd} value={sido.cd}>
                  {sido.cdNm}
                </option>
              ))}
            </select>
            <select
              id="iq-sgg1"
              value={form.hopeSggCd1}
              onChange={update('hopeSggCd1')}
              disabled={!form.hopeSidoCd1}
            >
              <option value="">시/군/구 선택</option>
              {sggList1.map((sgg) => (
                <option key={sgg.cd} value={sgg.cd}>
                  {sgg.cdNm}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className={styles.field}>
          <label htmlFor="iq-sido2">
            2차 희망지역 <span className={styles.req}>*</span>
          </label>
          <div className={styles.regionSelectRow}>
            <select id="iq-sido2" value={form.hopeSidoCd2} onChange={handleSidoChange(2)}>
              <option value="">시/도 선택</option>
              {sidoList.map((sido) => (
                <option key={sido.cd} value={sido.cd}>
                  {sido.cdNm}
                </option>
              ))}
            </select>
            <select
              id="iq-sgg2"
              value={form.hopeSggCd2}
              onChange={update('hopeSggCd2')}
              disabled={!form.hopeSidoCd2}
            >
              <option value="">시/군/구 선택</option>
              {sggList2.map((sgg) => (
                <option key={sgg.cd} value={sgg.cd}>
                  {sgg.cdNm}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={styles.field}>
        <label htmlFor="iq-message">
          가맹문의 내용 <span className={styles.req}>*</span>
        </label>
        <textarea
          id="iq-message"
          rows={4}
          maxLength={300}
          placeholder="문의하실 내용을 자유롭게 남겨주세요"
          value={form.message}
          onChange={update('message')}
        />
        <span className={styles.charCount}>{form.message.length}/300</span>
      </div>

      <div className={styles.consentBox}>
        <p className={styles.consentTitle}>[개인정보 수집 및 이용 동의 상세 내용]</p>

        <p className={styles.consentLabel}>{CONSENT_TEXT.purposeTitle}</p>
        <p className={styles.consentText}>{CONSENT_TEXT.purposeBody}</p>

        <p className={styles.consentLabel}>{CONSENT_TEXT.itemsTitle}</p>
        <p className={styles.consentText}>{CONSENT_TEXT.itemsBody}</p>
        <p className={styles.consentNote}>{CONSENT_TEXT.itemsNote}</p>

        <p className={styles.consentLabel}>{CONSENT_TEXT.retentionTitle}</p>
        {CONSENT_TEXT.retentionBody.map((line) => (
          <p className={styles.consentText} key={line}>
            {line}
          </p>
        ))}

        <p className={styles.consentNote}>{CONSENT_TEXT.refusalTitle}</p>
        {CONSENT_TEXT.refusalBody.map((line) => (
          <p className={styles.consentText} key={line}>
            {line}
          </p>
        ))}
      </div>

      <label className={styles.agreeRow} htmlFor="iq-agree">
        <input id="iq-agree" type="checkbox" checked={form.agree} onChange={update('agree')} />
        <span>
          위 개인정보 수집 및 이용에 동의합니다. <span className={styles.req}>*</span>
        </span>
      </label>

      {error && <p className={styles.errorMsg}>{error}</p>}

      <div className={styles.actions}>
        <button type="button" className={styles.backBtn} onClick={handleGoBack} disabled={submitting}>
          이전 화면
        </button>
        <button type="submit" className={styles.submitBtn} disabled={submitting}>
          {submitting ? '제출 중...' : '신청하기'}
        </button>
      </div>
    </form>
  );
}
